import { Search, SlidersHorizontal } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";

import { StockAdjustmentDesktopTable } from "./stock-adjustment-desktop-table";
import { StockAdjustmentMobileList } from "./stock-adjustment-mobile-list";
import { filterAdjustments } from "./stock-adjustment-utils";

import type { StockAdjustment } from "@artisancode/api-types";

interface Props {
  adjustments: StockAdjustment[];
  searchQuery: string;
  onSearchQueryChange: (q: string) => void;
  itemTypeFilter: string;
  onItemTypeFilterChange: (type: string) => void;
  statusFilter: string;
  onStatusFilterChange: (status: string) => void;
  bucketFilter: string;
  onBucketFilterChange: (bucket: string) => void;
  onCancelRequest: (adj: StockAdjustment) => void;
}

export function StockAdjustmentTableCard({
  adjustments,
  searchQuery,
  onSearchQueryChange,
  itemTypeFilter,
  onItemTypeFilterChange,
  statusFilter,
  onStatusFilterChange,
  bucketFilter,
  onBucketFilterChange,
  onCancelRequest,
}: Props) {
  const isMobile = useIsMobile();
  const rows = filterAdjustments(adjustments, {
    query: searchQuery,
    itemType: itemTypeFilter,
    status: statusFilter,
    bucket: bucketFilter,
  });

  return (
    <Card className="shadow-xs">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-primary" />
            <span>Riwayat Penyesuaian Stok</span>
          </CardTitle>

          {/* Filters */}
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2 w-full sm:w-auto">
            <div className="relative col-span-2 w-full sm:w-56">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Cari no. / barang..."
                value={searchQuery}
                onChange={(e) => onSearchQueryChange(e.target.value)}
                className="h-9 sm:h-8 pl-8 text-xs"
              />
            </div>

            <Select
              value={itemTypeFilter}
              onValueChange={onItemTypeFilterChange}
            >
              <SelectTrigger className="h-9 sm:h-8 w-full sm:w-32 text-xs">
                <SelectValue placeholder="Tipe Barang" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">
                  Semua Tipe
                </SelectItem>
                <SelectItem value="raw_material" className="text-xs">
                  Bahan Baku
                </SelectItem>
                <SelectItem value="product" className="text-xs">
                  Produk Jadi
                </SelectItem>
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={onStatusFilterChange}>
              <SelectTrigger className="h-9 sm:h-8 w-full sm:w-32 text-xs">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">
                  Semua Status
                </SelectItem>
                <SelectItem value="completed" className="text-xs">
                  Selesai
                </SelectItem>
                <SelectItem value="cancelled" className="text-xs">
                  Dibatalkan
                </SelectItem>
              </SelectContent>
            </Select>

            <Select value={bucketFilter} onValueChange={onBucketFilterChange}>
              <SelectTrigger className="h-9 sm:h-8 w-full sm:w-32 text-xs">
                <SelectValue placeholder="Lokasi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">
                  Semua Lokasi
                </SelectItem>
                <SelectItem value="on_hand" className="text-xs">
                  Di Toko
                </SelectItem>
                <SelectItem value="repacking" className="text-xs">
                  Di Bar
                </SelectItem>
                <SelectItem value="rider" className="text-xs">
                  Rider
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-0">
        {isMobile ? (
          <StockAdjustmentMobileList
            rows={rows}
            onCancelRequest={onCancelRequest}
          />
        ) : (
          <StockAdjustmentDesktopTable
            rows={rows}
            onCancelRequest={onCancelRequest}
          />
        )}
      </CardContent>
    </Card>
  );
}
